import { useState, useCallback } from "react";
import {
  createAppointmentPaymentIntent_api,
  verifyPaymentStatus_api,
} from "../api/api_calls/payment_apiCalls";

export const usePaymentProcessing = () => {
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(false);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);

  // Create payment intent for appointment booking - payments go directly to platform
  const createPaymentIntent = useCallback(async (appointmentData) => {
    setIsCreatingPaymentIntent(true);
    setError(null);

    try {
      const response =
        await createAppointmentPaymentIntent_api(appointmentData);

      if (response?.data) {
        setPaymentIntent({
          clientSecret: response.data.clientSecret,
          paymentIntentId: response.data.paymentIntentId,
        });
        setPaymentDetails(response.data.paymentDetails);

        return {
          success: true,
          clientSecret: response.data.clientSecret,
          paymentIntentId: response.data.paymentIntentId,
          paymentDetails: response.data.paymentDetails,
        };
      } else {
        throw new Error("Invalid response from payment intent creation");
      }
    } catch (err) {
      const errorMessage = err.message || "Failed to create payment intent";
      setError(errorMessage);

      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsCreatingPaymentIntent(false);
    }
  }, []);

  // Verify payment status and get appointment details
  const verifyPaymentStatus = useCallback(
    async (paymentIntentId, maxRetries = 5, retryDelay = 2000) => {
      setIsVerifyingPayment(true);
      setError(null);

      try {
        let attempt = 0;

        // Retry logic for payment verification (webhook processing might take time)
        while (attempt < maxRetries) {
          attempt++;

          try {
            const response = await verifyPaymentStatus_api(paymentIntentId);

            if (response?.data) {
              const { paymentStatus, transaction } = response.data;

              // If payment failed
              if (paymentStatus !== "succeeded") {
                throw new Error(`Payment ${paymentStatus}. Please try again.`);
              }

              // If payment succeeded and transaction exists with appointment
              if (paymentStatus === "succeeded" && transaction?.appointment) {
                return {
                  success: true,
                  paymentStatus,
                  appointmentCreated: true,
                  appointment: transaction.appointment,
                  transaction,
                };
              }

              // If payment succeeded but transaction/appointment not yet created (webhook still processing)
              if (
                paymentStatus === "succeeded" &&
                (!transaction || !transaction.appointment) &&
                attempt < maxRetries
              ) {
                await new Promise((resolve) => setTimeout(resolve, retryDelay));
                continue;
              }

              // If we've exhausted retries but payment succeeded without appointment
              if (
                paymentStatus === "succeeded" &&
                (!transaction || !transaction.appointment)
              ) {
                return {
                  success: true,
                  paymentStatus,
                  appointmentCreated: false,
                  transaction,
                  message:
                    "Payment successful. Appointment is being processed.",
                };
              }
            }
          } catch (attemptError) {
            // If this is the last attempt, throw the error
            if (attempt === maxRetries) {
              throw attemptError;
            }

            // Wait before retrying
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }

        // If we get here, all retries failed
        throw new Error("Payment verification failed after multiple attempts");
      } catch (err) {
        const errorMessage = err.message || "Failed to verify payment status";
        setError(errorMessage);

        return {
          success: false,
          error: errorMessage,
        };
      } finally {
        setIsVerifyingPayment(false);
      }
    },
    [],
  );

  // Reset payment state
  const resetPaymentState = useCallback(() => {
    setPaymentIntent(null);
    setPaymentDetails(null);
    setError(null);
  }, []);

  return {
    // State
    isCreatingPaymentIntent,
    isVerifyingPayment,
    paymentIntent,
    paymentDetails,
    error,

    // Actions
    createPaymentIntent,
    verifyPaymentStatus,
    resetPaymentState,
  };
};
